/**
 * Conflicts Detector Component
 * 
 * Main orchestrator component for the Smart Conflicts Detector feature.
 * Displays:
 * - Page title and subtitle
 * - Alert banner with conflict count
 * - Stats cards (critical, high, medium, resolved)
 * - Auto-resolve toggle and options
 * - Conflict list with filters
 */

import React, { useState, useRef, useCallback } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import { 
  IConflict, 
  TConflictSeverity, 
  IAutoResolveSettings,
  ISeverityCounts 
} from "../../types/conflictsDetector.types";
import { 
  mockConflicts, 
  mockConflictStats, 
  mockAutoResolveSettings 
} from "../../mocks/conflictsDetector.mock";
import AlertBanner from "./AlertBanner";
import ConflictStats from "./ConflictStats";
import AutoResolveSection from "./AutoResolveSection";
import SeverityFilterPills from "./SeverityFilterPills";
import ConflictCard from "./ConflictCard";
import styles from "../../appointmentsPage.module.css";

/**
 * ConflictsDetector Component
 * 
 * Main component for displaying and managing scheduling conflicts.
 */
const ConflictsDetector: React.FC = () => {
  // State
  const [conflicts, setConflicts] = useState<IConflict[]>(mockConflicts);
  const [stats, setStats] = useState(mockConflictStats);
  const [autoResolveSettings, setAutoResolveSettings] = useState<IAutoResolveSettings>(mockAutoResolveSettings);
  const [selectedSeverities, setSelectedSeverities] = useState<TConflictSeverity[]>(['critical', 'high', 'medium']);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  
  const conflictsSectionRef = useRef<HTMLDivElement>(null);

  // Calculate severity counts
  const severityCounts: ISeverityCounts = {
    critical: conflicts.filter(c => c.severity === 'critical').length,
    high: conflicts.filter(c => c.severity === 'high').length,
    medium: conflicts.filter(c => c.severity === 'medium').length
  };

  const totalConflicts = severityCounts.critical + severityCounts.high + severityCounts.medium;

  // Filter conflicts based on selected severities
  const filteredConflicts = conflicts.filter(c => selectedSeverities.includes(c.severity));

  // Toast helper
  const showToast = useCallback((messageKey: string) => {
    setToastMessage(t(messageKey));
    setTimeout(() => setToastMessage(null), 3000);
  }, []);

  // Handlers
  const handleScrollToConflicts = () => {
    conflictsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleToggleAutoResolve = () => {
    setAutoResolveSettings(prev => {
      const newEnabled = !prev.enabled;
      showToast(newEnabled ? "CONFLICTS_TOAST_AUTO_ENABLED" : "CONFLICTS_TOAST_AUTO_DISABLED");
      return { ...prev, enabled: newEnabled };
    });
  };

  const handleToggleAutoOption = (optionId: string) => {
    setAutoResolveSettings(prev => ({
      ...prev,
      options: prev.options.map(opt => 
        opt.id === optionId ? { ...opt, enabled: !opt.enabled } : opt
      )
    }));
  };

  const handleToggleSeverity = (severity: TConflictSeverity) => {
    setSelectedSeverities(prev => {
      if (prev.includes(severity)) {
        // Don't allow removing all filters
        if (prev.length === 1) return prev;
        return prev.filter(s => s !== severity);
      }
      return [...prev, severity];
    });
  };

  const handleApplySolution = (conflictId: string, solutionId: string) => {
    // Remove the resolved conflict
    setConflicts(prev => prev.filter(c => c.id !== conflictId));
    
    // Update stats
    const resolvedConflict = conflicts.find(c => c.id === conflictId);
    if (resolvedConflict) {
      setStats(prev => ({
        ...prev,
        [resolvedConflict.severity]: prev[resolvedConflict.severity] - 1,
        autoResolved: prev.autoResolved + 1
      }));
    }
    
    showToast("CONFLICTS_TOAST_RESOLVED");
  };

  const handleResolveManually = (conflictId: string) => {
    // In real app, this would open a modal for manual resolution
    console.log("Manual resolve:", conflictId);
  };

  const handleIgnore = (conflictId: string) => {
    // Remove the ignored conflict
    setConflicts(prev => prev.filter(c => c.id !== conflictId));
    
    // Update stats
    const ignoredConflict = conflicts.find(c => c.id === conflictId);
    if (ignoredConflict) {
      setStats(prev => ({
        ...prev,
        [ignoredConflict.severity]: prev[ignoredConflict.severity] - 1
      }));
    }
    
    showToast("CONFLICTS_TOAST_IGNORED");
  };

  return (
    <div className={styles.conflictsDetectorContainer}>
      {/* Page Header */}
      <div className={styles.conflictsPageHeader}>
        <h1 className={styles.conflictsPageTitle}>{t("CONFLICTS_PAGE_TITLE")}</h1>
        <p className={styles.conflictsPageSubtitle}>{t("CONFLICTS_PAGE_SUBTITLE")}</p>
      </div>

      {/* Alert Banner */}
      <AlertBanner
        totalConflicts={totalConflicts}
        severityCounts={severityCounts}
        onHandleNow={handleScrollToConflicts}
      />

      {/* Stats Grid */}
      <ConflictStats stats={stats} />

      {/* Auto-Resolve Section */}
      <AutoResolveSection
        settings={autoResolveSettings}
        onToggleEnabled={handleToggleAutoResolve}
        onToggleOption={handleToggleAutoOption}
      />

      {/* Conflicts List Section */}
      <div className={styles.conflictsSection} ref={conflictsSectionRef}>
        <div className={styles.conflictsSectionHeader}>
          <h2 className={styles.conflictsSectionTitle}>
            <span>⚡</span>
            <span>{t("CONFLICTS_SECTION_TITLE")}</span>
          </h2>
          <SeverityFilterPills
            selectedSeverities={selectedSeverities}
            severityCounts={severityCounts}
            onToggleSeverity={handleToggleSeverity}
          />
        </div>

        {/* Conflict Cards */}
        {filteredConflicts.length > 0 ? (
          filteredConflicts.map(conflict => (
            <ConflictCard
              key={conflict.id}
              conflict={conflict}
              onApplySolution={handleApplySolution}
              onResolveManually={handleResolveManually}
              onIgnore={handleIgnore}
            />
          ))
        ) : (
          <div className={styles.conflictsEmptyState}>
            <div className={styles.conflictsEmptyIcon}>✨</div>
            <div className={styles.conflictsEmptyTitle}>{t("CONFLICTS_EMPTY_TITLE")}</div>
            <div className={styles.conflictsEmptyDescription}>{t("CONFLICTS_EMPTY_DESC")}</div>
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className={styles.conflictToast}>
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default ConflictsDetector;
